import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router'
import type { Audience, PatientFilter, PatientPerPage, PatientSort } from './api'

/**
 * O estado da lista de pacientes mora na URL (RN-PAC-15, P-PAC-10): segmento, busca, ordem,
 * página, tamanho e filtros adicionais (`/patients?filter=pending&search=adri`). A última URL fica
 * guardada por clínica na sessão do navegador, para a lista voltar igual depois de uma ficha.
 * A busca salva é do navegador, por pessoa e por clínica; não há endpoint.
 */

const FILTERS: readonly PatientFilter[] = ['pending', 'incomplete', 'all', 'archived']
const SORTS: readonly PatientSort[] = ['urgency', 'name', '-name', 'patientSince', '-patientSince']
const PER_PAGES: readonly PatientPerPage[] = [10, 25, 50]
const AUDIENCES: readonly Audience[] = ['patient', 'student', 'employee', 'third_party']

/**
 * A busca como a API aceita (`patients/search.ts`): até 120 caracteres e até 6 termos. O que passar
 * disso é cortado aqui, para a lista não trocar de lugar com um 422.
 */
export const MAX_SEARCH_TERMS = 6
export function normalizeSearch(value: string): string {
  return value
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, MAX_SEARCH_TERMS)
    .join(' ')
    .slice(0, 120)
}

export interface PatientListState {
  /** O público escolhido no segmento (RN-PAC-39); vazio é Pacientes. */
  audience: Audience | ''
  /** `null` enquanto a pessoa não escolheu: a tela abre no que tem pendência (RN-PAC-12). */
  filter: PatientFilter | null
  search: string
  sort: PatientSort
  page: number
  perPage: PatientPerPage
  practitionerId: string
  procedureId: string
  from: string
  to: string
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

function parse(params: URLSearchParams): PatientListState {
  const filter = params.get('filter') as PatientFilter | null
  const sort = params.get('sort') as PatientSort | null
  const page = Number(params.get('page'))
  const perPage = Number(params.get('perPage')) as PatientPerPage
  const date = (key: string) => (ISO_DATE.test(params.get(key) ?? '') ? params.get(key)! : '')
  const audience = params.get('audience') as Audience | null
  return {
    audience: audience && audience !== 'patient' && AUDIENCES.includes(audience) ? audience : '',
    filter: filter && FILTERS.includes(filter) ? filter : null,
    search: normalizeSearch(params.get('search') ?? ''),
    sort: sort && SORTS.includes(sort) ? sort : 'urgency',
    page: Number.isInteger(page) && page >= 1 ? page : 1,
    perPage: PER_PAGES.includes(perPage) ? perPage : 10,
    practitionerId: params.get('practitionerId') ?? '',
    procedureId: params.get('procedureId') ?? '',
    from: date('from'),
    to: date('to'),
  }
}

/** Só o que difere do padrão vai para a URL. */
function serialize(state: PatientListState): URLSearchParams {
  const params = new URLSearchParams()
  if (state.audience) params.set('audience', state.audience)
  if (state.filter) params.set('filter', state.filter)
  if (state.search) params.set('search', state.search)
  if (state.sort !== 'urgency') params.set('sort', state.sort)
  if (state.page > 1) params.set('page', String(state.page))
  if (state.perPage !== 10) params.set('perPage', String(state.perPage))
  if (state.practitionerId) params.set('practitionerId', state.practitionerId)
  if (state.procedureId) params.set('procedureId', state.procedureId)
  if (state.from) params.set('from', state.from)
  if (state.to) params.set('to', state.to)
  return params
}

const listKey = (clinicId: string) => `wx-patients-list:${clinicId}`

/** A última URL da lista nesta clínica, para restaurar ao voltar (`''` quando não há). */
export function readStoredList(clinicId: string): string {
  try {
    return window.sessionStorage.getItem(listKey(clinicId)) ?? ''
  } catch {
    return ''
  }
}

function storeList(clinicId: string, search: string) {
  try {
    window.sessionStorage.setItem(listKey(clinicId), search)
  } catch {
    /* sem storage */
  }
}

export function usePatientListState(clinicId: string) {
  const [params, setParams] = useSearchParams()
  const state = parse(params)
  const current = params.toString()

  // Guarda cada estado visitado, inclusive o vazio: voltar para "sem filtro" também é uma escolha.
  useEffect(() => {
    storeList(clinicId, current)
  }, [clinicId, current])

  const update = useCallback(
    (patch: Partial<PatientListState>) => {
      setParams(
        (previous) => {
          const next = { ...parse(previous), ...patch }
          // Mudar o recorte volta à primeira página.
          if (!('page' in patch)) next.page = 1
          return serialize(next)
        },
        { replace: true },
      )
    },
    [setParams],
  )

  return { state, update }
}

// Busca salva -----------------------------------------------------------------------------------

const SAVED_LIMIT = 8
const savedKey = (userId: string, clinicId: string) =>
  `wx-patients-saved-searches:${userId}:${clinicId}`

function readSaved(key: string): string[] {
  try {
    const raw = JSON.parse(window.localStorage.getItem(key) ?? '[]') as unknown
    return Array.isArray(raw) ? raw.filter((item): item is string => typeof item === 'string') : []
  } catch {
    return []
  }
}

/** As buscas salvas desta pessoa nesta clínica: guardadas no navegador (P-PAC-10). */
export function useSavedSearches(userId: string, clinicId: string) {
  const key = savedKey(userId, clinicId)
  const [saved, setSaved] = useState(() => readSaved(key))

  const write = useCallback(
    (next: string[]) => {
      setSaved(next)
      try {
        window.localStorage.setItem(key, JSON.stringify(next))
      } catch {
        /* sem storage */
      }
    },
    [key],
  )

  return {
    saved,
    save: (search: string) =>
      write([search, ...saved.filter((item) => item !== search)].slice(0, SAVED_LIMIT)),
    remove: (search: string) => write(saved.filter((item) => item !== search)),
  }
}
